import { Component } from '@angular/core';
import { StitchMakerService, StitchPattern, SavedStitchCell } from '../../services/stitch-maker.service';
import { PaletteItem } from '../../models/palette-item.model';
import { STITCH_SYMBOLS, StitchSymbol } from '../../models/stitch-symbols';

export type StitchType = 'full' | 'half' | 'quarter' | 'three-quarter' | 'backstitch' | 'french-knot';

export interface StitchCell {
  paletteItemId: string | null;
  type: StitchType;
}

@Component({
    selector: 'app-stitch-maker',
    templateUrl: './stitch-maker.component.html',
    styleUrls: ['./stitch-maker.component.scss'],
    standalone: false
})
export class StitchMakerComponent {
  x = 100;
  y = 100;
  grid: StitchCell[][] = [];

  palette: PaletteItem[] = [];
  activePaletteItemId: string | null = null;
  selectedStitchType: StitchType = 'full';

  // draft color used by the "+" button to seed new palette items
  draftColor = '#a3cef1';

  // editor state
  editingPaletteItemId: string | null = null;

  recentPaletteItemIds: string[] = [];

  showGridLines = true;
  patterns: StitchPattern[] = [];
  patternName = '';
  author = '';
  selectedPatternId: string | null = null;
  showSaveModal = false;
  showPatternsModal = false;
  zoomLevel = 1;
  panX = 0;
  panY = 0;

  private gestureMode: 'idle' | 'pan' | 'pinch' = 'idle';
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private gestureStartTouchX = 0;
  private gestureStartTouchY = 0;
  private gestureStartPanX = 0;
  private gestureStartPanY = 0;
  private sheetDragStartY = 0;
  sheetState: 'peek' | 'full' = 'peek';

  // lookup maps — rebuilt whenever palette changes
  paletteItemById: Map<string, PaletteItem> = new Map();
  readonly symbolById: Map<string, StitchSymbol> = new Map(
    STITCH_SYMBOLS.map(s => [s.id, s] as const)
  );
  readonly stitchSymbols = STITCH_SYMBOLS;

  constructor(private stitchMakerService: StitchMakerService) {
    this.createGrid();
    this.rebuildPaletteIndex();
    this.loadPatterns();
  }

  trackByPaletteId(_i: number, item: PaletteItem) { return item.id; }

  openPaletteEditor(id: string) {
    this.editingPaletteItemId = id;
    this.activePaletteItemId = id;
  }

  closePaletteEditor() {
    this.editingPaletteItemId = null;
  }

  activateChip(id: string) {
    this.activePaletteItemId = id;
    this.recentPaletteItemIds = [
      id,
      ...this.recentPaletteItemIds.filter(x => x !== id),
    ].slice(0, 3);
  }

  /** Rough luminance check — true means the color is "light" so we should
   *  render the symbol in dark for contrast. */
  isLightColor(hex: string): boolean {
    const h = hex.replace('#', '');
    if (h.length < 6) return true;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
  }

  isSymbolTakenByOther(symbolId: string, excludingItemId: string): boolean {
    return this.palette.some(p => p.symbolId === symbolId && p.id !== excludingItemId);
  }

  createGrid() {
    this.grid = Array.from({ length: this.y }, () =>
      Array.from({ length: this.x }, () => ({ paletteItemId: null, type: 'full' as StitchType }))
    );
  }

  rebuildPaletteIndex() {
    this.paletteItemById = new Map(this.palette.map(p => [p.id, p] as const));
  }

  colorFor(paletteItemId: string | null): string {
    if (!paletteItemId) return '';
    return this.paletteItemById.get(paletteItemId)?.color ?? '';
  }

  symbolMarkupFor(paletteItemId: string | null): string {
    if (!paletteItemId) return '';
    const item = this.paletteItemById.get(paletteItemId);
    if (!item) return '';
    return this.symbolById.get(item.symbolId)?.svg ?? '';
  }

  /** Returns the first symbol id in STITCH_SYMBOLS not yet used in the palette.
   *  If all symbols are used, wraps to the first symbol (duplicates allowed). */
  nextUnusedSymbolId(palette: PaletteItem[]): string {
    const used = new Set(palette.map(p => p.symbolId));
    for (const s of STITCH_SYMBOLS) {
      if (!used.has(s.id)) return s.id;
    }
    return STITCH_SYMBOLS[0].id;
  }

  // --- palette CRUD, paint flow, persistence: filled in by later tasks ---

  onCellClick(row: number, col: number) {
    if (!this.activePaletteItemId) return;
    const cell = this.grid[row][col];
    const sameItem = cell.paletteItemId === this.activePaletteItemId;
    const sameType = cell.type === this.selectedStitchType;
    if (sameItem && sameType) {
      cell.paletteItemId = null;
      cell.type = 'full';
      return;
    }
    cell.paletteItemId = this.activePaletteItemId;
    cell.type = this.selectedStitchType;
  }

  addPaletteItem() {
    const item: PaletteItem = {
      id: this.generateId(),
      color: this.draftColor,
      symbolId: this.nextUnusedSymbolId(this.palette),
    };
    this.palette = [...this.palette, item];
    this.rebuildPaletteIndex();
    this.activateChip(item.id);
  }

  updatePaletteItem(id: string, patch: Partial<PaletteItem>) {
    this.palette = this.palette.map(p => p.id === id ? { ...p, ...patch, id: p.id } : p);
    this.rebuildPaletteIndex();
  }

  deletePaletteItem(id: string) {
    const count = this.countCellsUsingPaletteItem(id);
    if (count > 0) {
      const msg = `This will erase ${count} stitch${count === 1 ? '' : 'es'} using this palette entry. Continue?`;
      if (!window.confirm(msg)) return;
    }
    for (const row of this.grid) {
      for (const cell of row) {
        if (cell.paletteItemId === id) {
          cell.paletteItemId = null;
          cell.type = 'full';
        }
      }
    }
    this.palette = this.palette.filter(p => p.id !== id);
    this.rebuildPaletteIndex();
    this.recentPaletteItemIds = this.recentPaletteItemIds.filter(x => x !== id);
    if (this.activePaletteItemId === id) this.activePaletteItemId = null;
    if (this.editingPaletteItemId === id) this.editingPaletteItemId = null;
  }

  countCellsUsingPaletteItem(id: string): number {
    let n = 0;
    for (const row of this.grid) {
      for (const cell of row) {
        if (cell.paletteItemId === id) n++;
      }
    }
    return n;
  }

  private generateId(): string {
    // crypto.randomUUID() is available in modern browsers (Chromium, Firefox)
    return (crypto as any).randomUUID ? (crypto as any).randomUUID() : Math.random().toString(36).slice(2);
  }

  private toSavedCells(grid: StitchCell[][]): SavedStitchCell[] {
    return this.flattenGrid(grid).map(c => ({ paletteItemId: c.paletteItemId, type: c.type }));
  }

  private fromSavedCells(flat: SavedStitchCell[], width: number, height: number): StitchCell[][] {
    const cells: StitchCell[] = flat.map(c => ({
      paletteItemId: c.paletteItemId ?? null,
      type: (c.type as StitchType) ?? 'full',
    }));
    return this.unflattenGrid(cells, width, height);
  }

  savePattern() {
    const pattern: StitchPattern = {
      name: this.patternName,
      author: this.author,
      width: this.x,
      height: this.y,
      grid: this.toSavedCells(this.grid),
      palette: this.palette,
    };
    this.stitchMakerService.addPattern(pattern).then(() => this.loadPatterns());
  }

  loadPatterns() {
    this.stitchMakerService.getAllPatterns().subscribe(patterns => {
      this.patterns = patterns;
    });
  }

  selectPattern(pattern: StitchPattern) {
    this.selectedPatternId = pattern.id || null;
    this.patternName = pattern.name;
    this.author = pattern.author || '';
    this.x = pattern.width;
    this.y = pattern.height;
    this.palette = (pattern.palette || []).map(p => ({ ...p }));
    this.rebuildPaletteIndex();
    this.grid = this.fromSavedCells(pattern.grid || [], this.x, this.y);
    this.recentPaletteItemIds = [];
    this.activePaletteItemId = null;
    this.editingPaletteItemId = null;
    this.showPatternsModal = false;
  }

  updatePattern() {
    if (!this.selectedPatternId) return;
    const pattern: Partial<StitchPattern> = {
      name: this.patternName,
      author: this.author,
      width: this.x,
      height: this.y,
      grid: this.toSavedCells(this.grid),
      palette: this.palette,
    };
    this.stitchMakerService.updatePattern(this.selectedPatternId, pattern).then(() => this.loadPatterns());
  }

  deletePattern(id: string) {
    this.stitchMakerService.deletePattern(id).then(() => this.loadPatterns());
    if (this.selectedPatternId === id) {
      this.selectedPatternId = null;
      this.patternName = '';
      this.author = '';
      this.palette = [];
      this.rebuildPaletteIndex();
      this.createGrid();
    }
  }

  // --- zoom ---
  zoomIn() { if (this.zoomLevel < 3) this.zoomLevel += 0.1; }
  zoomOut() { if (this.zoomLevel > 0.2) this.zoomLevel -= 0.1; }

  setSheetState(state: 'peek' | 'full') {
    this.sheetState = state;
  }

  onSheetTouchStart(e: TouchEvent, _viewportHeight?: number) {
    if (e.touches.length !== 1) return;
    this.sheetDragStartY = e.touches[0].clientY;
  }

  onSheetTouchEnd(e: TouchEvent, viewportHeight?: number) {
    const vh = viewportHeight ?? window.innerHeight;
    const endY = e.changedTouches?.[0]?.clientY ?? e.touches?.[0]?.clientY ?? this.sheetDragStartY;
    this.sheetState = endY < vh / 2 ? 'full' : 'peek';
  }

  /** Computes a zoom that fits the whole grid in the given viewport, then
   *  resets pan to 0. Cells are rendered at 24px each. */
  fitToScreen(viewportWidthPx: number, viewportHeightPx: number) {
    const gridW = this.x * 24;
    const gridH = this.y * 24;
    const ideal = Math.min(viewportWidthPx / gridW, viewportHeightPx / gridH);
    const clamped = Math.min(5, Math.max(0.2, ideal));
    this.zoomLevel = clamped;
    this.panX = 0;
    this.panY = 0;
  }

  gridTransform(): string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`;
  }

  pinchDistance(t1: { clientX: number; clientY: number }, t2: { clientX: number; clientY: number }): number {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  onCanvasTouchStart(e: TouchEvent) {
    if (e.touches.length === 2) {
      this.gestureMode = 'pinch';
      this.pinchStartDist = this.pinchDistance(e.touches[0], e.touches[1]);
      this.pinchStartZoom = this.zoomLevel;
      return;
    }
    if (e.touches.length === 1) {
      const target = e.touches[0].target as HTMLElement | null;
      const startedOnCell = !!target && (target.classList?.contains('grid-cell') ||
        !!target.closest?.('.grid-cell'));
      if (startedOnCell) {
        this.gestureMode = 'idle';
        return;
      }
      this.gestureMode = 'pan';
      this.gestureStartTouchX = e.touches[0].clientX;
      this.gestureStartTouchY = e.touches[0].clientY;
      this.gestureStartPanX = this.panX;
      this.gestureStartPanY = this.panY;
    }
  }

  onCanvasTouchMove(e: TouchEvent) {
    if (this.gestureMode === 'pinch' && e.touches.length === 2) {
      const d = this.pinchDistance(e.touches[0], e.touches[1]);
      if (this.pinchStartDist > 0) {
        const next = this.pinchStartZoom * (d / this.pinchStartDist);
        this.zoomLevel = Math.min(5, Math.max(0.2, next));
      }
      e.preventDefault();
      return;
    }
    if (this.gestureMode === 'pan' && e.touches.length === 1) {
      this.panX = this.gestureStartPanX + (e.touches[0].clientX - this.gestureStartTouchX);
      this.panY = this.gestureStartPanY + (e.touches[0].clientY - this.gestureStartTouchY);
      e.preventDefault();
    }
  }

  onCanvasTouchEnd(_e: TouchEvent) {
    this.gestureMode = 'idle';
  }

  onCanvasWheel(e: WheelEvent) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const next = this.zoomLevel * (1 - e.deltaY * 0.001);
    this.zoomLevel = Math.min(5, Math.max(0.2, next));
  }

  // grid flatten helpers used by save/load
  flattenGrid(grid: StitchCell[][]): StitchCell[] {
    return ([] as StitchCell[]).concat(...grid);
  }
  unflattenGrid(flat: StitchCell[], width: number, height: number): StitchCell[][] {
    const result: StitchCell[][] = [];
    for (let i = 0; i < height; i++) {
      result.push(flat.slice(i * width, (i + 1) * width));
    }
    return result;
  }
}
